import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatRow, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { orderBy } from 'lodash';
import { firstValueFrom, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ExcelExportService } from '../../../core/data/excel-export.service';
import { IBoard } from '../../../core/models/board.model';
import { ICurrentBoard } from '../../../core/models/current-board.model';
import { IDisplayMetric } from '../../../core/models/display-metric.model';
import { IFilterData } from '../../../core/models/filter-data.model';
import { IProductAttributeRow } from '../../../core/models/product-attribute-row.model';
import { IProduct } from '../../../core/models/product.model';
import {
    actionStoreApplyFilters,
    actionStoreSetAttributeDisplayMetrics,
    actionStoreSetAttributeTableSort
} from '../../../core/store/store.actions';
import { IState } from '../../../core/store/store.model';
import {
    selectAttributeDisplayMetrics,
    selectAttributeTableSort,
    selectBoardData,
    selectFilterData
} from '../../../core/store/store.selectors';
import { defaultDisplayAttributes } from '../../../core/models/default-display-attributes.const';
import { IProductDisplayAttributeMetric } from '../../../core/models/product-display-attribute-metric.model';
import { AttributeService } from '../../../shared/services/attribute.service';
import { PercentageDecimalPlaces } from '../../../shared/models/percentage-decimal-places.enum';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'lynkd-pattern-attribute-table',
    templateUrl: './attribute-table.component.html',
    styleUrls: ['./attribute-table.component.scss']
})
export class AttributeTableComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren(MatRow) public rows!: QueryList<MatRow>;

    public readonly unitColumns: Array<string> = [
        'Intk Options TY',
        'CC Count TY',
        'CC Count LY',
        'AWS U LY',
        'AWS U TY',
        'AWS TY',
        'AWS LY',
        'ATS U LY',
        'ATS U TY',
        'CSOH U LY',
        'CSOH U TY',
        'Intk Options LY',
        'Sls Clear U LY',
        'Sls Clear U TY',
        'Sls Promo U LY',
        'Sls Promo U TY',
        'Sls Reg U LY',
        'Sls Reg U TY',
        'Sls U LY',
        'Sls U LY-1',
        'Sls U TY',
        'Str ATS U LY',
        'Str ATS U TY',
        'Str Intk U LY',
        'Str Intk U TY',
        'Str OSOH U LY',
        'Str OSOH U TY',
        'AWS U PLY',
        'CSOH U PLY',
        'On Sls U LY',
        'On Sls U PLY',
        'On Sls U TY',
        'OSOH U LY',
        'OSOH U PLY',
        'OSOH U TY',
        'Rec Options LY',
        'Rec Options TY',
        'Rt Sls U LY',
        'Rt Sls U PLY',
        'Rt Sls U TY',
        'SKU Count LY',
        'SKU Count PLY',
        'Tr Sls U LY',
        'Tr Sls U PLY',
        'Tr Sls U TY',
        'Ttl Sls U LY',
        'Ttl Sls U PLY',
        'Ttl Sls U TY',
        'Ttl Str Rec U LY',
        'Ttl Str Rec U TY',
        'WH CSOH U'
    ];

    public readonly randValueMetrics: Array<string> = [
        'CSOH V LY',
        'CSOH V TY',
        'ATS V LY',
        'ATS V TY',
        'Sls Val TY',
        'Sls Val LY',
        'GPR TY',
        'GPR LY',
        'ATS Val LY',
        'ATS Val TY',
        'CSOH Val LY',
        'CSOH Val PLY',
        'CSOH Val TY',
        'GP Val LY',
        'GP Val PLY',
        'GP Val TY',
        'On Sls Val LY',
        'On Sls Val PLY',
        'On Sls Val TY',
        'OSOH Val LY',
        'OSOH Val PLY',
        'OSOH Val TY',
        'Rt Sls Val LY',
        'Rt Sls Val PLY',
        'Rt Sls Val TY',
        'Tr Sls Val LY',
        'Tr Sls Val PLY',
        'Tr Sls Val TY',
        'Ttl Sls Val',
        'Ttl Sls Val LY',
        'Ttl Sls Val PLY',
        'Ttl Str Rec Val LY',
        'Ttl Str Rec Val TY',
        'WH CSOH V',
        'Ttl CSOH Val LY',
        'Ttl CSOH Val TY',
        'Ttl CSOH Val PLY',
        'Sales Val Growth LY',
        'GP Val Growth LY'
    ];

    public readonly percentageMetrics: Array<string> = [
        'Str SM R G%',
        'Str Rec U Var LY %',
        'Sls U TY Cont %',
        'Sls U LY Cont %',
        'Sls U Var LY %',
        'GPR Var LY %',
        'Sls V TY Cont %',
        'Sls V LY Cont %',
        'Sls V Var LY %',
        'AWS Var LY %',
        'AWC Var LY %',
        'CSOH U Var LY %',
        'CSOH V Var LY %',
        'CSOH U LY Cont %',
        'CSOH U TY Cont %',
        'CSOH V TY Cont %',
        'CSOH V LY Cont %',
        'SOR % TY',
        'SOR % LY',
        'ATS V Var LY %',
        'AWC TY',
        'AWC LY',
        'GP % TY',
        'GP % LY',
        'ATS Val Contrb%',
        'ATS Val G% LY',
        'AWC % LY',
        'AWC % PLY',
        'AWC % TY',
        'CSOH U G% LY',
        'CSOH Val G% LY',
        'GP % PLY',
        'GP Val G% LY',
        'On Sls U Cont %',
        'On Sls Val Cont %',
        'Rec Options G% LY',
        'Rt Sls U % of Ttl LY',
        'Rt Sls U % of Ttl TY',
        'Rt Sls U Cont %',
        'Rt Sls U G% LY',
        'Rt Sls Val % of Ttl LY',
        'Rt Sls Val % of Ttl TY',
        'Rt Sls Val Cont %',
        'Rt Sls Val G% LY',
        'SKU Count %G LY',
        'SOR% LY',
        'SOR% PLY',
        'SOR% TY',
        'Tr Sls U % of Ttl LY',
        'Tr Sls U % of Ttl TY',
        'Tr Sls Val Cont %',
        'Ttl Sls U Cont %',
        'Ttl Sls U G% LY',
        'Ttl Sls Val Cont %',
        'Ttl Sls Val G% LY',
        'Ttl Str Rec U Cont %',
        'Ttl Str Rec U G% LY',
        'Ttl Str Rec Val Cont %',
        'Ttl Str Rec Val G% LY',
        'Str SM R G%'
    ];

    public isLoadingTableData: boolean = false;

    public get columns(): Array<IProductDisplayAttributeMetric> {
        if (!this._columns) {
            this._columns = [];
        }
        return this._columns;
    }

    public set columns(value: Array<IProductDisplayAttributeMetric>) {
        this._columns = value;
        // this.defaultProductDisplayAttributeMetrics$ = this._attributeService.getDefaultProductDisplayAttributeMetrics();
        // if (!value) {
        //     this.defaultProductDisplayAttributeMetrics$.subscribe((data: Array<IProductDisplayAttributeMetric>) => {
        //         this._columns = data;
        //     });
        //     return;
        // }
        // this.availableProductDisplayMetrics$ = this._attributeService.getAvailableProductDisplayAttributeMetrics();
        // this.availableProductDisplayMetrics$.subscribe((data: Array<IProductDisplayAttributeMetric>) => {
        //     this._columns = value.map((t: IProductDisplayAttributeMetric) =>
        //         data.find((x: IProductDisplayAttributeMetric) => x.column_name === t.column_name));
        //     // this._columns = data;
        // });
    }

    public get attributeRows(): Array<IProductAttributeRow> {
        return this._attributeRows;
    }

    @Input()
    public set attributeRows(value: Array<IProductAttributeRow>) {
        this.tableSource.sort = this.sort;
        this._attributeRows = orderBy(
            value.filter((t: IProductAttributeRow) => t.attribute_value !== 'Totals'),
            (x: IProductAttributeRow) => x.sls_u_ty,
            'desc'
        );
        while (this._attributeRows && this._attributeRows.length < this.minTableRows) {
            this._attributeRows.push({ attribute_value: '' });
        }

        const rows: Array<IProductAttributeRow> = value.filter((t: IProductAttributeRow) => t.attribute_value === 'Totals');
        rows.push(...this._attributeRows);
        this.tableSource.data = rows;
    }

    public tableSource: MatTableDataSource<IProductAttributeRow> = new MatTableDataSource();

    @ViewChild(MatSort)
    public sort: MatSort;

    @Input()
    public attribute: string;

    /**
     * Heading for the top-left cell only. Kept separate from `attribute` on purpose: `attribute` is
     * the attribute's real display_name and still drives behaviour elsewhere, so overwriting it to
     * relabel the heading would change more than the label.
     */
    @Input()
    public attributeHeading: string;

    @Input()
    public attributeColumn: string;
    public currentBoard: ICurrentBoard = {
        user_id: '',
        board_id: 0,
        board_name: '',
        time_ranges: undefined as string,
        time_periods: [],
        time_years: [],
        time_quarters: [],
        time_months: [],
        location_companies: [],
        location_divisions: [],
        location_areas: [],
        location_regions: [],
        location_stores: [],
        fields: '',
        departments: [],
        sub_departments: [],
        categories: [],
        product_types: [],
        meta_datas: {},
        metric_ranges: [],
        display_metrics: [],
        type: '',
        exclusions: [],
        display_attributes: defaultDisplayAttributes,
        display_attribute_metrics: []
    };
    public hasShortenedAndFormattedRandValues: boolean = environment.hasCommaSeparatedThousands;
    public hasShortenedAndFormattedPercentages: boolean = environment.hasShortenedAndFormattedPercentages;
    public hasCommaSeparatedThousands: boolean = environment.hasCommaSeparatedThousands;
    public hasShortenedAndFormattedUnitValues: boolean = environment.hasShortenedAndFormattedUnitValues;
    /**
     * Precision handed to PercentValuePipe for the percentage columns. Read from the environment
     * rather than hardcoded at the call site, and passed explicitly so the pipe's own default of 2
     * stays untouched for the Maps tables that call it with no argument.
     */
    public attributePercentageDecimals: PercentageDecimalPlaces = environment.attributePercentageDecimals;

    private _columns: Array<IProductDisplayAttributeMetric>;
    private filterData: IFilterData = {
        display_attributes: defaultDisplayAttributes
    };
    private readonly destroyed: Subject<void> = new Subject<void>();
    private minTableRows: number;
    private _attributeRows: Array<IProductAttributeRow> = [];
    private readonly displayNameMap: Map<string, number> = new Map([
        [Breakpoints.XSmall, 4],
        [Breakpoints.Small, 4],
        [Breakpoints.Medium, 4],
        [Breakpoints.Large, 4],
        [Breakpoints.XLarge, 7]
    ]);

    public constructor(
        breakpointObserver: BreakpointObserver,
        private readonly _excelExportService: ExcelExportService,
        private readonly _store: Store<IState>,
        private readonly _attributeService: AttributeService,
        private readonly _router: Router,
        private readonly _cdr: ChangeDetectorRef
    ) {
        breakpointObserver
            .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
            .pipe(takeUntil(this.destroyed))
            .subscribe((result: BreakpointState) => {
                for (const query of Object.keys(result.breakpoints)) {
                    if (result.breakpoints[query]) {
                        this.minTableRows = this.displayNameMap.get(query) ?? 4;
                    }
                }
            });
    }

    public ngOnInit(): void {
        this._store
            .select(selectBoardData)
            .pipe(takeUntil(this.destroyed))
            .subscribe((result: IBoard) => {
                this.currentBoard.board_id = result.board_id;
                this.currentBoard.board_name = result.board_name;
                this.currentBoard.type = result.type;
            });
        this._store
            .select(selectFilterData)
            .pipe(takeUntil(this.destroyed))
            .subscribe((result: IFilterData) => {
                const fields: Array<string> = ['image_id', 'style_id', 'description'];
                this.filterData = JSON.parse(JSON.stringify(result));
                this.filterData.sort_order = this.filterData.sort_order.length ? this.filterData.sort_order : 'sls_u:desc';
                this.filterData.time_period =
                    this.filterData.time_period.length ||
                    this.filterData.time_year.length ||
                    this.filterData.time_quarter.length ||
                    this.filterData.time_month.length
                        ? this.filterData.time_period
                        : '';
                this.currentBoard.time_periods = this.filterData.time_period;
                this.currentBoard.time_ranges = this.filterData.time_range;
                this.currentBoard.time_years = this.filterData.time_year;
                this.currentBoard.time_quarters = this.filterData.time_quarter;
                this.currentBoard.time_months = this.filterData.time_month;
                this.currentBoard.location_companies = this.filterData.location_company;
                this.currentBoard.location_divisions = this.filterData.location_division;
                this.currentBoard.location_areas = this.filterData.location_area;
                this.currentBoard.location_regions = this.filterData.location_region;
                this.currentBoard.location_stores = this.filterData.location_store;
                this.currentBoard.departments = this.filterData.department;
                this.currentBoard.sub_departments = this.filterData.sub_department;
                this.currentBoard.categories = this.filterData.category;
                this.currentBoard.product_types = this.filterData.product_type;
                this.currentBoard.meta_datas = this.filterData.meta_data;
                this.currentBoard.metric_ranges = this.filterData.metric_range;
                this.currentBoard.display_attributes = this.filterData.display_attributes;
                this.currentBoard.exclusions = this.filterData.exclusions as Array<IProduct>;
                this.currentBoard.fields = this.filterData.display_metrics
                    ? fields
                          .concat(this.filterData.display_metrics.map((prop: IDisplayMetric) => prop.metric_name))
                          .filter((value: string, index: number, self: Array<string>) => self.indexOf(value) === index)
                          .join(',')
                    : '';
            });

        this._store
            .pipe(
                select(selectAttributeDisplayMetrics),
                distinctUntilChanged(
                    (previous: { display_metrics: Array<IProductDisplayAttributeMetric>; },
                        current: { display_metrics: Array<IProductDisplayAttributeMetric>; }) => {
                            const prevSelectedMetrics: string = JSON.stringify(previous.display_metrics);
                            const currSelectedMetrics: string = JSON.stringify(current.display_metrics);

                            // Only trigger load when there's new metric data to load
                            if (prevSelectedMetrics !== currSelectedMetrics) {
                                this.isLoadingTableData = true;
                            }

                            return prevSelectedMetrics === currSelectedMetrics;
                        }
                ),
                takeUntil(this.destroyed)
            )
            // .select(selectAttributeDisplayMetrics)
            // .pipe(takeUntil(this.destroyed))
            .subscribe(async ({ display_metrics }: { display_metrics: Array<IProductDisplayAttributeMetric> }) => {
                // this._spinnerService.spin$.next(true);
                this.isLoadingTableData = true;

                // The timeout will ensure the spinner renders before the long processing starts
                // Gives Angular has time to process any DOM updates
                setTimeout(async () => {
                    if (!display_metrics) {
                        try {
                            const result: Array<IProductDisplayAttributeMetric> =
                            await this._attributeService.getDefaultProductDisplayAttributeMetrics();
                            this._store.dispatch(
                                actionStoreSetAttributeDisplayMetrics({
                                    displayMetrics: result
                                })
                            );
                            return;
                        } catch (error) {
                            // console.log("getDefaultProductDisplayAttributeMetrics Error: ", error);
                        }
                    }

                    try {
                        const allMetrics: Array<IProductDisplayAttributeMetric> =
                        await this._attributeService.getAvailableProductDisplayAttributeMetrics();

                        // Validate each stored metric against the available attribute metrics.
                        // Legacy attribute boards may carry gallery/table metric names that don't map to any
                        // attribute metric, which would otherwise produce undefined columns and crash the table.
                        const validColumns: Array<IProductDisplayAttributeMetric> = display_metrics
                            .map((t: IProductDisplayAttributeMetric) =>
                                allMetrics.find((x: IProductDisplayAttributeMetric) => x.column_name === t.column_name)
                            )
                            .filter((column: IProductDisplayAttributeMetric | undefined):
                                column is IProductDisplayAttributeMetric => Boolean(column));

                        // Keep only the valid metrics; if none are valid, fall back to the default attribute metrics.
                        this._columns = validColumns.length
                            ? validColumns
                            : await this._attributeService.getDefaultProductDisplayAttributeMetrics();

                        // If the stored metrics had to be corrected (invalid ones dropped, or defaults applied),
                        // push the corrected set back into the store so the display metrics modal stays in sync
                        // and reflects exactly what the table is showing.
                        const storedColumns: string = JSON.stringify(
                            display_metrics.map((t: IProductDisplayAttributeMetric) => t.column_name)
                        );
                        const correctedColumns: string = JSON.stringify(
                            this._columns.map((t: IProductDisplayAttributeMetric) => t.column_name)
                        );
                        if (storedColumns !== correctedColumns) {
                            this._store.dispatch(
                                actionStoreSetAttributeDisplayMetrics({
                                    displayMetrics: this._columns
                                })
                            );
                        }
                    } catch (error) {
                        // console.log("getAvailableProductDisplayAttributeMetrics error: ", error);
                    }
                }, 0);

                // this.isLoadingTableData = false;
                // this._spinnerService.spin$.next(false);
            });

        this._store
            .select(selectAttributeTableSort)
            .pipe(takeUntil(this.destroyed))
            .subscribe((tableSort: { sortOrder?: string; sortDirection?: 'asc' | 'desc' }) => {
                this.sortTable(tableSort.sortOrder, tableSort.sortDirection);
            });
    }

    public ngAfterViewInit(): void {
        this.tableSource.sort = this.sort;
        this.rows.changes.subscribe(() => {
            // All rows are rendered
            this._cdr.detectChanges(); // make sure view updates
            this.isLoadingTableData = false;
            // console.log("Stop Spinning this.isLoadingTableData: ", this.isLoadingTableData);
        });
    }

    public ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    public getRowColumns(): Array<string> {
        return ['attribute_value', ...this.columns.map((t: IProductDisplayAttributeMetric) => t.column_name)];
    }

    // public getTotal(name: string): number {
    //     return this._attributeRows
    //         .map((t: IProductAttributeRow) => t[name] as number)
    //         .reduce((total: number, value: number) => total + value, 0);
    //}
    public sortData(sortState: Sort): void {
        const direction: 'asc' | 'desc' = sortState.direction === '' ? 'desc' : sortState.direction;
        this._store.dispatch(actionStoreSetAttributeTableSort({ sortOrder: sortState.active, sortDirection: direction }));
        this.sortTable(sortState.active, direction);
    }

    public sortTable(sortOrder: string, sortDirection: 'asc' | 'desc'): void {
        let valueRows: Array<IProductAttributeRow> = this.tableSource.data.filter(
            (t: IProductAttributeRow) => t.attribute_value !== 'Totals' && t.attribute_value !== ''
        );
        valueRows = orderBy(valueRows, (x: IProductAttributeRow) => x[sortOrder], sortDirection);
        while (valueRows && valueRows.length < this.minTableRows) {
            valueRows.push({ attribute_value: '' });
        }

        const rows: Array<IProductAttributeRow> = this.tableSource.data.filter(
            (t: IProductAttributeRow) => t.attribute_value === 'Totals'
        );
        rows.push(...valueRows);
        this.tableSource = new MatTableDataSource<IProductAttributeRow>(rows);
        // this.tableSource.sortData(this.tableSource.data, this.tableSource.sort);
    }

    public async exportExcel(): Promise<void> {
        await this._excelExportService.exportToExcel({
            data: this.tableSource.data.slice().map((item: IProductAttributeRow) => {
                const { ...newItem }: IProductAttributeRow & { image_id?: string; image?: string | ArrayBuffer | null } = {
                    ...item
                };
                return newItem;
            }),
            fileName: `Product Attributes`,
            header: [
                {
                    header: 'Attribute',
                    key: 'attribute_value'
                },
                ...this.columns.map((t: IProductDisplayAttributeMetric) => ({
                    header: t.display_name,
                    key: t.column_name
                }))
            ]
        });
    }

    public async valueClicked(value: string): Promise<boolean> {
        if (value === 'Totals') {
            return;
        }
        // Navigate first. The listing only issues a product request for a filterData emission that
        // arrives once the gallery view is active -- on the attributes view it deliberately skips
        // the load -- so this dispatch has to land after the hop or the gallery never reloads.
        const navigated: boolean = await this._router.navigate(['/analytics', 'gallery']);

        // Read the store after navigating rather than snapshotting it before. Arriving on the
        // gallery dispatches its own filter state, and that dispatch is what seeds the gallery
        // display metrics when the app booted straight onto the Attributes tab -- nothing else on
        // that path seeds them. Building from a pre-navigation snapshot captured the empty list and
        // then overwrote the good one, so the gallery rendered with no metrics.
        const current: IFilterData = await firstValueFrom(this._store.select(selectFilterData));

        const appliedFilters: {
            filterData: IFilterData;
            boardData: IBoard;
        } = {
            boardData: {
                user_id: this.currentBoard.user_id,
                board_id: this.currentBoard.board_id,
                board_name: this.currentBoard.board_name,
                // Preserve the loaded board's owner across the round-trip.
                type: this.currentBoard.type
            },
            filterData: {
                time_period: current.time_period,
                time_range: current.time_range,
                time_year: current.time_year,
                time_quarter: current.time_quarter,
                time_month: current.time_month,
                // Carried through like every other time filter; omitting it silently dropped a
                // week selection on the hop to Gallery.
                time_week: current.time_week,
                location_company: current.location_company,
                location_division: current.location_division,
                location_area: current.location_area,
                location_region: current.location_region,
                location_store: current.location_store,
                sort_order: current.sort_order,
                display_metrics: current.display_metrics,
                department: current.department,
                sub_department: current.sub_department,
                category: current.category,
                product_type: current.product_type,
                // Clone so linking to gallery does not mutate the store/listing meta_data reference
                meta_data: JSON.parse(JSON.stringify(current.meta_data || {})),
                metric_range: current.metric_range,
                exclusions: this.currentBoard.exclusions,
                display_attributes: current.display_attributes
            }
        };
        if (this.attributeColumn === 'category_name') {
            appliedFilters.filterData.category = [value];
        } else if (this.attributeColumn === 'sub_department_name') {
            appliedFilters.filterData.sub_department = [value];
        } else if (this.attributeColumn === 'department_name') {
            appliedFilters.filterData.department = [value];
        } else if (this.attributeColumn === 'product_type_name') {
            appliedFilters.filterData.product_type = [value];
        } else {
            appliedFilters.filterData.meta_data[this.attributeColumn] = [value];
        }
        this._store.dispatch(actionStoreApplyFilters(appliedFilters));

        return navigated;
    }

    public drop(event: CdkDragDrop<IProductAttributeRow, IProductAttributeRow>): void {
        moveItemInArray(this._columns, event.previousIndex, event.currentIndex);
        this._store.dispatch(actionStoreSetAttributeDisplayMetrics({ displayMetrics: this._columns }));
    }
}
